import { useContext } from 'react'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { ArrowPathIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/solid'
import { equipmentMedia } from 'functions/equipmentUtils'
import { VerifyGroupIndividualWorkoutsMatchBase } from 'redux/slices/activeWorkoutV5'
import { IsActiveWorkoutSwappedSlot, SwapSlotExercise, UndoSlotExerciseSwap } from 'functions/workoutUtils'

export default function WorkoutV5PreviewComponent({
    exerciseHashMap = {},
    workoutPreviewData = null,
    setWorkoutPreviewData = () => {}
}) {

    const { ShowModal } = useContext(ModalContext)

    const workout = workoutPreviewData?.workout ?? null

    // Exercise instructions

    const ShowExerciseInstructionsModal = (exerciseId) => {
        const modalData = {
            key: ModalKey.ExerciseInstructions,
            fullscreen: true,
            exerciseId: exerciseId,
        }

        ShowModal(modalData)
    }

    // Exercise swap. Frontend only until the workout is ended, when the swapped slots are saved (same as the athlete app)

    const ShowExerciseSwapModal = (phaseIndex, slotIndex) => {
        const modalData = {
            key: ModalKey.ExerciseLibrary,
            size: 'third',
            subHeaderText: 'Select an exercise to swap in',
            OnExerciseSelected: (exercise) => __UpdateSlot(phaseIndex, slotIndex, (slot) => SwapSlotExercise(slot, exercise.id))
        }

        ShowModal(modalData)
    }

    const UndoExerciseSwap = (phaseIndex, slotIndex) => {
        __UpdateSlot(phaseIndex, slotIndex, (slot) => UndoSlotExerciseSwap(slot))
    }

    // Group workouts are saved from each group individual's workout, so their slots get the same change as the base workout

    const __UpdateSlot = (phaseIndex, slotIndex, UpdateSlot) => {
        setWorkoutPreviewData((prevWorkoutPreviewData) => {
            const updatedWorkoutPreviewData = JSON.parse(JSON.stringify(prevWorkoutPreviewData))

            UpdateSlot(updatedWorkoutPreviewData.workout.phases[phaseIndex].slots[slotIndex])

            if (updatedWorkoutPreviewData.kinesisWorkoutAssignmentType === 'group') {
                for (const groupIndividual of updatedWorkoutPreviewData.groupIndividualData ?? []) {
                    UpdateSlot(groupIndividual.workout.phases[phaseIndex].slots[slotIndex])
                }

                VerifyGroupIndividualWorkoutsMatchBase(updatedWorkoutPreviewData.workout, updatedWorkoutPreviewData.groupIndividualData)
            }

            return updatedWorkoutPreviewData
        })
    }
    
    if (
        Object.keys(exerciseHashMap).length === 0
        || workout === null
    ) {
        return null
    }

    return (
        <div className='space-y-8'>
            {workout.phases.map((phase, phaseIndex) => (
                <div
                    key={phaseIndex}
                    className='space-y-4'
                >
                    {phase.type === 'circuit' &&
                        <div className='flex pr-2'>
                            <p className='text-sm text-gray-500 flex-grow'>{phase.name}</p>
                            <p className="text-sm text-gray-500">{phase.sets} round{phase.sets > 1 ? 's' : ''}</p>
                        </div>
                    }

                    {phase.slots.map((slot, slotIndex) => {
                        const exercise = exerciseHashMap[slot.exerciseId]

                        if (!exercise) {
                            return null
                        }

                        return (
                            <div
                                key={slotIndex}
                                className="flex items-center gap-x-4 p-2 bg-white rounded-2xl shadow-md cursor-pointer"
                                onClick={() => ShowExerciseInstructionsModal(exercise.id)}
                            >
                                <img
                                    src={exercise.imageUrl}
                                    alt="Exercise Thumbnail"
                                    className="w-16 h-16 object-cover rounded-lg"
                                    onError={(e) => { e.target.style.display = 'none' }}
                                />

                                <div className='space-y-1 overflow-hidden cursor-pointer'>
                                    <div className="text-base">{exercise.name}</div>

                                    {exercise?.equipment && exercise.equipment.length > 0 &&
                                        <div className='flex gap-x-1'>
                                            {exercise.equipment.split(',').map((equipment, index) => (
                                                <img
                                                    key={index}
                                                    src={equipmentMedia[equipment]?.imageURL}
                                                    alt="Equipment Icon"
                                                    className="h-6"
                                                />
                                            ))}
                                        </div>
                                    }
                                </div>

                                <div className='flex-1' />

                                {IsActiveWorkoutSwappedSlot(slot) === false && (
                                    <div
                                        className='h-14 w-14 flex flex-col items-center justify-center rounded-xl font-normal pr-2 cursor-pointer'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            ShowExerciseSwapModal(phaseIndex, slotIndex)
                                        }}
                                    >
                                        <ArrowPathIcon className="h-8 w-8 text-gray-600 mt-1 mb-0.5" />
                                        <p className="text-sm text-gray-700">Swap</p>
                                    </div>
                                )}

                                {IsActiveWorkoutSwappedSlot(slot) === true && (
                                    <div
                                        className='h-14 w-14 flex flex-col items-center justify-center rounded-xl font-normal'
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            UndoExerciseSwap(phaseIndex, slotIndex)
                                        }}
                                    >
                                        <ArrowUturnLeftIcon className='text-gray-700 w-6 h-6 mt-1 mb-0.5' />
                                        <p className="text-xxs text-gray-700">Undo</p>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}
